'use client';

import React, { useState } from "react";
import { Clock, Loader2, Copy, Check } from "lucide-react";
import { RoleChip } from "./RoleChip";
import { initialsOf, formatPhone, useTeam, errText, inviteJoinMessage, inviteWhatsAppUrl } from "../internals";
import { useToast } from "./Toaster";

const daysLeft = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

export const InviteRow = ({ invite, onRevoked }) => {
  const { storage, currentUser } = useTeam();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const canRevoke = ["owner", "admin", "manager"].includes(currentUser.role);
  const left = daysLeft(invite.expires_at);

  const copySteps = async () => {
    try {
      await navigator.clipboard.writeText(inviteJoinMessage({ name: invite.name, phone: invite.phone }));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.show("Couldn't copy — long-press the WhatsApp button instead.", "error");
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await storage.revokeInvite(invite.id);
      toast.show(`Invite to ${invite.name} cancelled.`, "success");
      onRevoked && onRevoked();
    } catch (e) {
      toast.show(errText(e), "error");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`invite-row-${invite.id}`}
      className="flex items-center gap-3 rounded-lg border border-dashed border-zinc-300 bg-white px-3 py-3"
    >
      <span
        aria-hidden="true"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-semibold text-amber-700"
      >
        {initialsOf(invite.name)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-zinc-900">{invite.name || "—"}</span>
        <span className="block truncate text-xs text-zinc-500">{formatPhone(invite.phone)}</span>
      </span>
      <span className="hidden items-center gap-1 text-[11px] text-zinc-400 sm:inline-flex">
        <Clock size={11} />
        {left === null ? "" : left === 0 ? "Expires today" : `${left} day${left === 1 ? "" : "s"} left`}
      </span>
      <RoleChip role={invite.role} />
      <a
        data-testid={`invite-whatsapp-${invite.id}`}
        href={inviteWhatsAppUrl({ name: invite.name, phone: invite.phone })}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-green-700"
      >
        Send joining steps
      </a>
      <button
        type="button"
        data-testid={`invite-copy-${invite.id}`}
        onClick={copySteps}
        title="Copy the joining steps"
        className="inline-flex items-center gap-1 rounded-md border border-zinc-300 px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
      >
        {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
        {copied ? "Copied" : "Copy"}
      </button>
      {canRevoke && (
        <button
          type="button"
          data-testid={`invite-revoke-${invite.id}`}
          onClick={revoke}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-300 px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
        >
          {busy && <Loader2 size={12} className="animate-spin" />}
          Cancel invite
        </button>
      )}
    </div>
  );
};
